import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { LANGUAGES } from '../i18n/languages';
import type { SupportedLanguage } from '../i18n';

interface Props {
  /** Topbar pill (flag + locale code). Without this, renders the wider
   *  settings-page version with the native name. */
  compact?: boolean;
}

export function LanguagePicker({ compact }: Props) {
  const { i18n, t } = useTranslation();
  const current = ((i18n.language || 'en').split('-')[0] || 'en') as SupportedLanguage;
  const meta = LANGUAGES.find((l) => l.code === current);

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <button
        type="button"
        aria-label={t('languagePicker.selectLanguage')}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={
          compact
            ? 'inline-flex h-9 items-center gap-1.5 rounded-full px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
            : 'inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-card px-3 text-sm font-medium hover:bg-muted transition-colors'
        }
      >
        {compact ? (
          <>
            <span className="text-base leading-none">{meta?.flag ?? '🌐'}</span>
            <span className="hidden sm:inline text-[11px] uppercase tracking-wider font-semibold">
              {current.toUpperCase()}
            </span>
          </>
        ) : (
          <>
            <Globe size={14} className="text-muted-foreground" aria-hidden />
            <span className="text-base leading-none">{meta?.flag ?? '🌐'}</span>
            <span>{meta?.nativeName ?? 'English'}</span>
          </>
        )}
        <ChevronDown className="h-3 w-3 opacity-60" aria-hidden />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={
              // compact = topbar (anchor right so popover opens leftward into the page).
              // non-compact = settings card on the left (anchor left so it opens rightward,
              // not back under the sidebar).
              `absolute top-11 z-50 max-h-80 w-56 overflow-y-auto
               rounded-2xl border border-border bg-popover text-popover-foreground
               shadow-lg backdrop-blur p-1 ${compact ? 'right-0' : 'left-0'}`
            }
          >
            {LANGUAGES.map((l) => {
              const active = l.code === current;
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      void i18n.changeLanguage(l.code);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-left transition-colors ${
                      active
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'hover:bg-muted text-foreground/80'
                    }`}
                  >
                    <span className="text-base leading-none">{l.flag}</span>
                    <span className="flex-1 truncate">{l.nativeName}</span>
                    {active && <Check className="h-3.5 w-3.5 text-primary" aria-hidden />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
